<template>
  <v-container id="broker-list" fluid tag="section">
    <SomePendingRecreatePosition :somePendingRecreatePosition="somePendingRecreatePosition" />

    <v-row>
      <template v-if="!loading">
        <v-col v-for="(broker, idx) in items" :key="idx" cols="12" xl="3" lg="4" md="6" xs="12">
          <BrokerCard :broker="broker" @edit="editBroker" />
        </v-col>
      </template>

      <v-col cols="12" xl="3" lg="4" md="6" xs="12">
        <BrokerCard :loading="loading" @create="createBroker" />
      </v-col>
    </v-row>

    <div v-if="showSwitchHideZeroBrokers" class="d-flex align-end ml-2 mb-6">
      <v-switch
        v-model="user.preferences.hideZeroBrokers"
        hide-details
        dense
        class="mt-2"
        @change="changeHideZeroBrokersPreference"
      >
        <template v-slot:label>
          <span>
            {{ $t(`views.brokers.brokerAccounts.hideZeroBrokers`) }}
          </span>
        </template>
      </v-switch>
      <div class="hide-zero-brokers-info-icon">
        <InfoIcon
          :size="16"
          :maxWidth="310"
          :text="$t(`views.brokers.brokerAccounts.hideZeroBrokersInfo`)"
        />
      </div>
    </div>

    <BrokerModal
      :isModalActive.sync="isBrokerModalActive"
      :editItem="editItem"
      :editing="editing"
      @saved="brokerSaved"
    />

    <WelcomeBrokerModal
      :isModalActive.sync="isWelcomeBrokerModalActive"
      :welcomeItem="welcomeItem"
    />
  </v-container>
</template>

<script>
import { getBrokersDetail, getPositionsLean, updateUser } from '@/lib/api.js'
import { mapState, mapActions } from 'vuex'
import { get, pick } from 'lodash'
import gtag from '@/lib/gtag'
import BrokerCard from './components/brokers/accounts/BrokerCard'
import BrokerModal from './components/brokers/accounts/BrokerModal'
import WelcomeBrokerModal from './components/brokers/accounts/WelcomeBrokerModal'
import InfoIcon from '@/views/components/common/InfoIcon'
import visibilityMixin from '@/views/components/core/visibilityMixin'
import SomePendingRecreatePosition from '@/views/components/common/positions/SomePendingRecreatePosition.vue'

export default {
  components: {
    BrokerCard,
    BrokerModal,
    WelcomeBrokerModal,
    InfoIcon,
    SomePendingRecreatePosition,
  },
  mixins: [visibilityMixin],
  data: () => ({
    isBrokerModalActive: false,
    isWelcomeBrokerModalActive: false,
    loading: false,
    editing: false,
    editItem: {},
    brokers: [],
    items: [],
    somePendingRecreatePosition: false,
    loadTimeout: null,
    welcomeItem: {},
  }),
  computed: {
    ...mapState(['user']),
    showSwitchHideZeroBrokers() {
      if (get(this.user, 'preferences.hideZeroBrokers') === true) return true // evitamos que lo tenga activado y no lo vea

      const someBrokerWithValues = this.brokers.some(
        (broker) => broker.totalCashValue !== 0 || broker.totalNotCashValue !== 0
      )
      if (!someBrokerWithValues) return false

      if (this.brokers.length <= 1) return false

      return true
    },
  },
  watch: {
    '$route.query': {
      handler() {
        if (this.$route.query.open) {
          if (this.$route.query.open === 'broker') {
            this.createBroker()
          }
        }
      },
      deep: true,
      immediate: true,
    },
    'user.preferences.hideZeroBrokers': {
      handler() {
        this.filterBrokersByZeroValue()
      },
    },
  },
  async created() {
    await this.loadBrokers()
  },
  async destroyed() {
    clearTimeout(this.loadTimeout)
  },
  methods: {
    ...mapActions({ updateUsersInfo: 'UPDATE_USERS_INFO' }),
    createBroker() {
      if (this.loading) return
      this.editing = false
      this.isBrokerModalActive = true

      gtag.pageview({
        pagePath: `/brokerAccounts/create`,
        pageTitle: `brokerAccounts|create`,
        pageContentGroup: 'modal',
      })
    },
    brokerSaved(broker) {
      this.welcomeItem = broker || {}
      this.loadBrokers({ brokerSaved: true })
    },
    async changeHideZeroBrokersPreference(value) {
      try {
        const data = pick(this.user, ['preferences'])
        data.preferences.hideZeroBrokers = value
        await updateUser({ uid: this.user.uid, data })
        await this.updateUsersInfo()
      } catch (error) {
        this.$root.$snackbar.error({ text: this.$t('common.saveError') })
        this.loading = false
      }
    },
    filterBrokersByZeroValue() {
      if (get(this.user, 'preferences.hideZeroBrokers', false)) {
        this.items = this.brokers.filter(
          (broker) => broker.totalCashValue !== 0 || broker.totalNotCashValue !== 0
        )
      } else {
        this.items = [...this.brokers]
      }
    },
    async loadBrokers({ brokerSaved } = {}) {
      this.loading = true
      try {
        const positions = await getPositionsLean({
          filter: {},
        })
        this.somePendingRecreatePosition = positions.data.some((item) => item.pendingRecreate)

        const brokers = await getBrokersDetail()
        this.brokers = brokers.data
        this.filterBrokersByZeroValue()

        if (!this.editing && brokerSaved && this.brokers.length === 1)
          this.isWelcomeBrokerModalActive = true

        clearTimeout(this.loadTimeout)
        if (this.somePendingRecreatePosition) {
          this.loadTimeout = setTimeout(() => {
            this.loadBrokers()
          }, 5000)
        }
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loading = false
    },
    editBroker(broker) {
      this.editing = true
      this.isBrokerModalActive = true
      this.editItem = JSON.parse(JSON.stringify(broker))

      gtag.pageview({
        pagePath: `/brokerAccounts/edit`,
        pageTitle: `brokerAccounts|edit`,
        pageContentGroup: 'modal',
      })
    },
  },
}
</script>

<style class="scss">
#broker-list {
  .hide-zero-brokers-info-icon {
    margin: 0px 0px 3px 10px;
  }
}
</style>
